import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DOCUMENT_TYPES,
  GENDERS,
  MARITAL_STATUSES,
  PERSON_ACADEMIC_LEVELS,
  PERSON_ACADEMIC_LEVEL_STATUSES,
  PERSON_BLOOD_TYPES,
  PERSON_STATUSES,
  type AddressResponse,
  type ContactType,
  type DocumentType,
  type PersonAcademicLevel,
  type PersonAcademicLevelStatus,
  type PersonBloodType,
  type PersonDetailResponse,
  type UpdatePersonRequest,
} from '@tabita/contracts';
import { formatDate } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { filesApi } from '@/services/files.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field, Spinner, StatusBadge } from '@/components/ui';
import { COUNTRY_CODES, countryName } from '@/data/country-codes';
import { Avatar } from '@/components/avatar';
import { PersonTabPanel, PersonTabs, type PersonTabDef } from './person-detail-tabs';
import { ActivitiesTab } from './activities-tab';
import { DonationsTab } from './donations-tab';
import { TransfersTab } from './transfers-tab';
import { FollowUpsTab } from './followups-tab';
import { GrowthTab } from './growth-tab';
import { PlaceholderTab } from './placeholder-tab';
import { HouseholdCard } from './household-card';
import { RelationshipsCard } from './relationships-card';
import { FamilyTreeCard } from './family-tree-card';
import { CustomFieldsCard } from './custom-fields-card';
import { ContactsCard } from './contacts-card';
import { CatalogPicker } from './catalog-picker';
import { UserAccountCard } from './user-account-card';
import { SendMessageDialog } from '../communications/send-message-dialog';

// `DocumentType` has no built-in "unknown" member (unlike `Gender`/`MaritalStatus`),
// since a missing document is a valid, common state — the empty option represents that.
const DOCUMENT_TYPE_OPTIONS = ['', ...DOCUMENT_TYPES] as const;

/**
 * One person: view, edit, archive and restore.
 *
 * The directory could previously only list and create. `peopleApi.get`,
 * `update` and `archive` existed, fully typed, and were called from nowhere —
 * you could add someone and then never open them again.
 *
 * Editing happens in place rather than in a dialog. A dialog suits creation,
 * where the fields are few and the task is one decision; correcting a record
 * means reading the current values while you change them, and a modal covers
 * exactly what you are checking against.
 */
export function PersonDetailPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  const canSendMessages =
    can('communications.campaign.create') && can('communications.campaign.send');

  const query = useQuery({
    queryKey: ['people', id],
    queryFn: ({ signal }) => peopleApi.get(id, signal),
  });

  /**
   * Invalidates the directory as well as this record.
   *
   * A name change or an archive alters the list this page was reached from.
   * Refreshing only the detail leaves the previous screen showing values the
   * user has just watched themselves change.
   */
  const onChanged = async (person: PersonDetailResponse, message: string) => {
    queryClient.setQueryData(['people', id], person);
    await queryClient.invalidateQueries({ queryKey: ['people'], exact: false });
    setNotice(message);
    setIsEditing(false);
  };

  const archive = useMutation({
    mutationFn: (reason: string) => peopleApi.archive(id, reason),
    onSuccess: (person) =>
      onChanged(person, t('people.person.archived', { values: { name: person.displayName } })),
  });

  const restore = useMutation({
    mutationFn: () => peopleApi.restore(id),
    onSuccess: (person) =>
      onChanged(person, t('people.person.restored', { values: { name: person.displayName } })),
  });

  const deletePerson = useMutation({
    mutationFn: (reason: string) => peopleApi.delete(id, reason),
    onSuccess: async (result) => {
      setDeleteError(null);
      if (result.outcome === 'DELETED') {
        // The row is gone — nothing left to show on this page.
        await queryClient.invalidateQueries({ queryKey: ['people'], exact: false });
        void navigate('/people', { replace: true });
        return;
      }
      await onChanged(
        result.person!,
        t('people.person.markedDeleted', { values: { name: result.person!.displayName } }),
      );
    },
    onError: (error: unknown) => setDeleteError(describeError(error, t)),
  });

  const uploadPhoto = useMutation({
    mutationFn: async (file: File) => {
      const session = await filesApi.requestUpload({
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        declaredSize: file.size,
        ownerType: 'Person',
        ownerId: id,
      });
      await filesApi.putBytes(session.uploadUrl, file);
      const uploaded = await filesApi.completeUpload(session.id, {
        fileName: file.name,
        ownerType: 'Person',
        ownerId: id,
      });
      return peopleApi.update(id, { photoFileId: uploaded.id });
    },
    onSuccess: (person) => {
      setPhotoError(null);
      return onChanged(person, t('people.photo.uploaded'));
    },
    onError: (error: unknown) => setPhotoError(describeError(error, t)),
  });

  if (query.isPending) return <Spinner label={t('common.states.loading')} />;

  if (query.isError) {
    return (
      <div className="page people-scope">
        <BackLink label={t('common.actions.back')} />
        <Alert tone="danger">{describeError(query.error, t)}</Alert>
      </div>
    );
  }

  const person = query.data;
  const isDeleted = person.deletedAt !== null;
  const isArchived = person.archivedAt !== null;
  // The aggregate refuses to edit, archive, restore or delete a person who
  // is already marked DELETED — `deletedAt` wins over `archivedAt` in
  // `assertNotArchived()`. Restore in particular is not offered here even
  // though `isArchived` is false: `restore()` itself only clears
  // `archivedAt`, and a DELETED person was never re-activatable by design.
  const isEditable = !isArchived && !isDeleted;

  const tabs: PersonTabDef[] = [
    { id: 'info', label: t('people.detail.tabs.info') },
    ...(can('people.relationship.read')
      ? [{ id: 'familyTree', label: t('people.detail.tabs.familyTree') }]
      : []),
    ...(can('attendance.record.read')
      ? [{ id: 'activities', label: t('people.detail.tabs.activities') }]
      : []),
    { id: 'notes', label: t('people.detail.tabs.notes'), disabled: true },
    { id: 'forms', label: t('people.detail.tabs.forms'), disabled: true },
    ...(can('giving.donation.read')
      ? [{ id: 'donations', label: t('people.detail.tabs.donations') }]
      : []),
    ...(can('people.followup.read')
      ? [{ id: 'followUps', label: t('people.detail.tabs.followUps') }]
      : []),
    ...(can('people.transfer.read')
      ? [{ id: 'transfers', label: t('people.detail.tabs.transfers') }]
      : []),
    ...(can('people.growthProgress.read')
      ? [{ id: 'growth', label: t('people.detail.tabs.growth') }]
      : []),
  ];

  return (
    <div className="page people-scope">
      <BackLink label={t('common.actions.back')} />

      <div className="person-card">
        <div className="person-card-header">
          <div className="person-header-identity">
            {can('people.person.update') && isEditable ? (
              <label className="avatar-upload" aria-label={t('people.photo.change')}>
                <Avatar
                  photoFileId={person.photoFileId}
                  displayName={person.displayName}
                  size={64}
                />
                <span className="avatar-upload-hint">{t('people.photo.change')}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) uploadPhoto.mutate(file);
                  }}
                />
              </label>
            ) : (
              <Avatar photoFileId={person.photoFileId} displayName={person.displayName} size={64} />
            )}
            <div>
              <h1 className="page-title">{person.displayName}</h1>
              <StatusBadge
                status={person.status}
                label={t(`people.person.status.${person.status}`)}
              />
              <p className="person-card-meta">
                {t('common.labels.createdAt')}: {formatDate(person.createdAt, locale)}
                {' · '}
                {t('common.labels.updatedAt')}: {formatDate(person.updatedAt, locale)}
              </p>
            </div>
          </div>

          <div className="page-actions">
            {canSendMessages && (
              <Button variant="secondary" onClick={() => setIsSendingMessage(true)}>
                {t('communications.sendMessage.action')}
              </Button>
            )}
            {can('people.person.update') && isEditable && !isEditing && activeTab === 'info' && (
              <Button onClick={() => setIsEditing(true)}>{t('common.actions.edit')}</Button>
            )}
          </div>
        </div>

        <ContactIcons contacts={person.contacts} />

        <PersonTabs
          tabs={tabs}
          active={activeTab}
          onSelect={setActiveTab}
          label={t('people.person.title')}
        />

        <div>
          {(notice || photoError || archive.isError || restore.isError || deleteError) && (
            <div className="person-card-alerts">
              {notice && <Alert tone="success">{notice}</Alert>}
              {photoError && <Alert tone="danger">{photoError}</Alert>}
              {archive.isError && <Alert tone="danger">{describeError(archive.error, t)}</Alert>}
              {restore.isError && <Alert tone="danger">{describeError(restore.error, t)}</Alert>}
              {deleteError && <Alert tone="danger">{deleteError}</Alert>}
            </div>
          )}

          <PersonTabPanel id="info" active={activeTab}>
            {/*
              An archived person cannot be edited — the aggregate refuses it, so
              the button is withheld rather than left to fail on submit.
            */}
            {isDeleted ? (
              <Alert tone="warning">{t('people.person.deletedHint')}</Alert>
            ) : (
              isArchived && <Alert tone="warning">{t('people.person.archivedHint')}</Alert>
            )}

            {isEditing ? (
              <PersonEditForm
                person={person}
                onCancel={() => setIsEditing(false)}
                onSaved={(updated) =>
                  void onChanged(
                    updated,
                    t('people.person.saved', { values: { name: updated.displayName } }),
                  )
                }
              />
            ) : (
              <dl className="detail-grid">
                <Detail label={t('people.person.firstName')} value={person.firstName} />
                <Detail label={t('people.person.middleName')} value={person.middleName} />
                <Detail label={t('people.person.lastName')} value={person.lastName} />
                <Detail label={t('people.person.preferredName')} value={person.preferredName} />
                <Detail
                  label={t('people.person.birthDate')}
                  value={person.birthDate ? formatDate(person.birthDate, locale) : null}
                />
                <Detail
                  label={t('common.labels.gender')}
                  value={t(`people.person.gender.${person.gender}`)}
                />
                <Detail
                  label={t('common.labels.maritalStatus')}
                  value={t(`people.person.maritalStatus.${person.maritalStatus}`)}
                />
                <Detail
                  label={t('common.labels.documentType')}
                  value={
                    person.documentType
                      ? t(`people.person.documentType.${person.documentType}`)
                      : null
                  }
                />
                <Detail
                  label={t('common.labels.documentNumber')}
                  value={person.documentNumber}
                  dir="ltr"
                />
                {/*
                  Contact details stay left-to-right whatever the page direction. An
                  email address or a phone number laid out RTL is visually scrambled.
                */}
                <Detail label={t('people.person.email')} value={person.primaryEmail} dir="ltr" />
                <Detail label={t('people.person.phone')} value={person.primaryPhone} dir="ltr" />
                <Detail label={t('people.person.address')} value={formatAddress(person.address)} />
                <Detail
                  label={t('common.labels.birthCountry')}
                  value={
                    person.birthCountryCode ? countryName(person.birthCountryCode, locale) : null
                  }
                />
              </dl>
            )}

            {!isEditing && (
              <section className="detail-section">
                <h2 className="detail-section-title">{t('people.contact.title')}</h2>
                <dl className="detail-grid">
                  <Detail
                    label={t('common.labels.personalEmail')}
                    value={person.personalEmail}
                    dir="ltr"
                  />
                  <Detail label={t('common.labels.workEmail')} value={person.workEmail} dir="ltr" />
                  <Detail
                    label={t('common.labels.secondaryEmail')}
                    value={person.secondaryEmail}
                    dir="ltr"
                  />
                </dl>
              </section>
            )}

            {!isEditing && (
              <section className="detail-section">
                <h2 className="detail-section-title">{t('people.academicWork.title')}</h2>
                <dl className="detail-grid">
                  <Detail
                    label={t('common.labels.academicLevel')}
                    value={
                      person.academicLevel
                        ? t(`people.person.academicLevel.${person.academicLevel}`)
                        : null
                    }
                  />
                  <Detail
                    label={t('common.labels.academicLevelStatus')}
                    value={
                      person.academicLevelStatus
                        ? t(`people.person.academicLevelStatus.${person.academicLevelStatus}`)
                        : null
                    }
                  />
                  <Detail
                    label={t('common.labels.profession')}
                    value={person.profession?.name ?? null}
                  />
                  <Detail
                    label={t('common.labels.occupation')}
                    value={person.occupation?.name ?? null}
                  />
                  <Detail
                    label={t('common.labels.economicSector')}
                    value={person.economicSector?.name ?? null}
                  />
                </dl>
              </section>
            )}

            {!isEditing && (
              <section className="detail-section">
                <h2 className="detail-section-title">{t('people.medical.title')}</h2>
                <dl className="detail-grid">
                  <Detail
                    label={t('common.labels.bloodType')}
                    value={
                      person.bloodType ? t(`people.person.bloodType.${person.bloodType}`) : null
                    }
                  />
                  <Detail
                    label={t('common.labels.medicalIndications')}
                    value={person.medicalIndications}
                  />
                </dl>
              </section>
            )}

            {!isEditing && (
              <>
                <HouseholdCard
                  personId={person.id}
                  household={person.household}
                  onChanged={() => void queryClient.invalidateQueries({ queryKey: ['people', id] })}
                />
                <RelationshipsCard
                  personId={person.id}
                  relationships={person.relationships}
                  onChanged={() => void queryClient.invalidateQueries({ queryKey: ['people', id] })}
                />
                <UserAccountCard
                  personId={person.id}
                  personEmail={person.primaryEmail}
                  personDisplayName={person.displayName}
                />
                <ContactsCard
                  personId={person.id}
                  contacts={person.contacts}
                  onChanged={() => void queryClient.invalidateQueries({ queryKey: ['people', id] })}
                />
                <CustomFieldsCard personId={person.id} />
              </>
            )}
          </PersonTabPanel>

          {can('people.relationship.read') && (
            <PersonTabPanel id="familyTree" active={activeTab}>
              <FamilyTreeCard
                personId={person.id}
                personDisplayName={person.displayName}
                personPhotoFileId={person.photoFileId}
                relationships={person.relationships}
                onChanged={() => void queryClient.invalidateQueries({ queryKey: ['people', id] })}
              />
            </PersonTabPanel>
          )}

          {can('attendance.record.read') && (
            <PersonTabPanel id="activities" active={activeTab}>
              <ActivitiesTab personId={person.id} />
            </PersonTabPanel>
          )}

          <PersonTabPanel id="notes" active={activeTab}>
            <PlaceholderTab label={t('people.detail.tabs.notes')} />
          </PersonTabPanel>
          <PersonTabPanel id="forms" active={activeTab}>
            <PlaceholderTab label={t('people.detail.tabs.forms')} />
          </PersonTabPanel>

          {can('giving.donation.read') && (
            <PersonTabPanel id="donations" active={activeTab}>
              <DonationsTab personId={person.id} />
            </PersonTabPanel>
          )}

          {can('people.followup.read') && (
            <PersonTabPanel id="followUps" active={activeTab}>
              <FollowUpsTab personId={person.id} />
            </PersonTabPanel>
          )}

          {can('people.transfer.read') && (
            <PersonTabPanel id="transfers" active={activeTab}>
              <TransfersTab personId={person.id} currentCampusId={person.campusId} />
            </PersonTabPanel>
          )}

          {can('people.growthProgress.read') && (
            <PersonTabPanel id="growth" active={activeTab}>
              <GrowthTab personId={person.id} />
            </PersonTabPanel>
          )}
        </div>

        <footer className="person-card-footer">
          <div className="person-card-footer-links">
            <button type="button" className="person-card-footer-link" disabled>
              {t('people.detail.directorySettings')}
            </button>
            {/* In-tab SPA navigation, not `target="_blank"` — a fresh tab
                starts with no in-memory auth state, and relying on
                `sessionStorage` cloning into it proved unreliable. The
                card's own "Back" link returns here. */}
            {isEditable && (
              <Link
                className="person-card-footer-link"
                to={`/people/${person.id}/print/credential`}
              >
                {t('people.print.credential')}
              </Link>
            )}
            {can('people.person.archive') &&
              !isDeleted &&
              (isArchived ? (
                <Button
                  variant="secondary"
                  loading={restore.isPending}
                  onClick={() => restore.mutate()}
                >
                  {t('common.actions.restore')}
                </Button>
              ) : (
                <ArchiveButton person={person} onConfirm={(reason) => archive.mutate(reason)} />
              ))}
            {can('people.person.delete') && !isDeleted && (
              <DeleteButton
                person={person}
                loading={deletePerson.isPending}
                onConfirm={(reason) => deletePerson.mutate(reason)}
              />
            )}
          </div>
          {can('people.person.update') && isEditable && !isEditing && activeTab !== 'info' && (
            <Button
              variant="primary"
              onClick={() => {
                setActiveTab('info');
                setIsEditing(true);
              }}
            >
              {t('common.actions.edit')}
            </Button>
          )}
        </footer>
      </div>

      {isSendingMessage && (
        <SendMessageDialog
          onClose={() => setIsSendingMessage(false)}
          onSent={() => {
            setIsSendingMessage(false);
            setNotice(t('communications.sendMessage.sent'));
          }}
          recipientIds={[person.id]}
          recipientLabel={person.displayName}
        />
      )}
    </div>
  );
}

function ContactIcons({ contacts }: { contacts: PersonDetailResponse['contacts'] }): ReactNode {
  const { t } = useI18n();

  const byType = (type: ContactType) => contacts.find((c) => c.type === type);
  const email = byType('EMAIL');
  const mobile = byType('MOBILE');
  const homePhone = byType('HOME_PHONE');
  const workPhone = byType('WORK_PHONE');

  const links = [
    email && {
      href: `mailto:${email.value}`,
      label: t('people.detail.contact.email'),
      icon: 'email' as const,
    },
    mobile && {
      href: `tel:${mobile.value}`,
      label: t('people.detail.contact.call'),
      icon: 'phone' as const,
    },
    homePhone && {
      href: `tel:${homePhone.value}`,
      label: t('people.detail.contact.call'),
      icon: 'phone' as const,
    },
    workPhone && {
      href: `tel:${workPhone.value}`,
      label: t('people.detail.contact.call'),
      icon: 'phone' as const,
    },
  ].filter((link): link is { href: string; label: string; icon: 'email' | 'phone' } =>
    Boolean(link),
  );

  if (links.length === 0) return null;

  return (
    <div className="person-contact-icons">
      {links.map((link, index) => (
        <a key={index} href={link.href} className="person-contact-icon" aria-label={link.label}>
          {link.icon === 'email' ? <EmailIcon /> : <PhoneIcon />}
        </a>
      ))}
    </div>
  );
}

function EmailIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

// --- Pieces ------------------------------------------------------------------

function BackLink({ label }: { label: string }) {
  return (
    <Link to="/people" className="back-link">
      {label}
    </Link>
  );
}

function Detail({
  label,
  value,
  dir,
}: {
  label: string;
  value: string | null;
  dir?: 'ltr';
}): ReactNode {
  return (
    <div className="detail-row">
      <dt className="detail-label">{label}</dt>
      <dd className="detail-value" dir={dir}>
        {value ?? <span className="cell-empty">—</span>}
      </dd>
    </div>
  );
}

/**
 * Archiving asks for a reason before it will proceed.
 *
 * The API requires one and the domain stores it, because "why is this family no
 * longer in the directory" is a question someone asks months later. Collecting
 * it in a confirmation step also slows down an action that is easy to trigger
 * by accident from a list.
 */
function ArchiveButton({
  person,
  onConfirm,
}: {
  person: PersonDetailResponse;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (!isOpen) {
    return (
      <Button variant="danger" onClick={() => setIsOpen(true)}>
        {t('common.actions.archive')}
      </Button>
    );
  }

  return (
    <form
      className="inline-confirm"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onConfirm(reason.trim());
      }}
    >
      <p>{t('people.person.archiveConfirm', { values: { name: person.displayName } })}</p>
      <Field
        label={t('people.person.archiveReason')}
        name="reason"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        required
        autoFocus
      />
      <Button type="button" variant="ghost" onClick={() => setIsOpen(false)}>
        {t('common.actions.cancel')}
      </Button>
      <Button type="submit" variant="danger" disabled={reason.trim().length === 0}>
        {t('common.actions.confirm')}
      </Button>
    </form>
  );
}

/**
 * Real deletion, gated behind its own reason-capture confirm — same UX as
 * `ArchiveButton`, since it's the same class of destructive, audited
 * action. The server decides whether this is a true removal or a
 * `DELETED` mark; the button doesn't know which until the response comes
 * back (see `PersonDetailPage`'s `deletePerson` mutation).
 */
function DeleteButton({
  person,
  loading,
  onConfirm,
}: {
  person: PersonDetailResponse;
  loading: boolean;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (!isOpen) {
    return (
      <button
        type="button"
        className="person-card-footer-link"
        data-tone="danger"
        onClick={() => setIsOpen(true)}
      >
        {t('common.actions.delete')}
      </button>
    );
  }

  return (
    <form
      className="inline-confirm"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onConfirm(reason.trim());
      }}
    >
      <p>{t('people.person.deleteConfirm', { values: { name: person.displayName } })}</p>
      <p className="field-hint">{t('people.person.deleteHint')}</p>
      <Field
        label={t('people.person.deleteReason')}
        name="delete-reason"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        required
        autoFocus
      />
      <Button type="button" variant="ghost" onClick={() => setIsOpen(false)}>
        {t('common.actions.cancel')}
      </Button>
      <Button
        type="submit"
        variant="danger"
        loading={loading}
        disabled={reason.trim().length === 0}
      >
        {t('common.actions.confirm')}
      </Button>
    </form>
  );
}

function PersonEditForm({
  person,
  onCancel,
  onSaved,
}: {
  person: PersonDetailResponse;
  onCancel: () => void;
  onSaved: (person: PersonDetailResponse) => void;
}) {
  const { t, locale } = useI18n();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Annotated (not asserted) so the union survives — inside the `useState`
  // object literal below, an inline `?? ''` here would otherwise widen to
  // plain `string` for lack of a contextual type.
  const initialDocumentType: DocumentType | '' = person.documentType ?? '';
  const initialAcademicLevel: PersonAcademicLevel | '' = person.academicLevel ?? '';
  const initialAcademicLevelStatus: PersonAcademicLevelStatus | '' =
    person.academicLevelStatus ?? '';
  const initialBloodType: PersonBloodType | '' = person.bloodType ?? '';

  const [form, setForm] = useState({
    firstName: person.firstName,
    middleName: person.middleName ?? '',
    lastName: person.lastName,
    preferredName: person.preferredName ?? '',
    birthDate: person.birthDate ?? '',
    gender: person.gender,
    maritalStatus: person.maritalStatus,
    documentType: initialDocumentType,
    documentNumber: person.documentNumber ?? '',
    status: person.status,
    communicationsOptOut: person.communicationsOptOut,
    addressLine1: person.address?.line1 ?? '',
    addressLine2: person.address?.line2 ?? '',
    addressCity: person.address?.city ?? '',
    addressRegion: person.address?.region ?? '',
    addressPostalCode: person.address?.postalCode ?? '',
    addressCountryCode: person.address?.countryCode ?? '',
    birthCountryCode: person.birthCountryCode ?? '',
    personalEmail: person.personalEmail ?? '',
    workEmail: person.workEmail ?? '',
    secondaryEmail: person.secondaryEmail ?? '',
    academicLevel: initialAcademicLevel,
    academicLevelStatus: initialAcademicLevelStatus,
    professionId: person.profession?.id ?? '',
    occupationId: person.occupation?.id ?? '',
    economicSectorId: person.economicSector?.id ?? '',
    bloodType: initialBloodType,
    medicalIndications: person.medicalIndications ?? '',
  });

  const addressChanged =
    form.addressLine1 !== (person.address?.line1 ?? '') ||
    form.addressLine2 !== (person.address?.line2 ?? '') ||
    form.addressCity !== (person.address?.city ?? '') ||
    form.addressRegion !== (person.address?.region ?? '') ||
    form.addressPostalCode !== (person.address?.postalCode ?? '') ||
    form.addressCountryCode !== (person.address?.countryCode ?? '');

  const addressIsBlank =
    !form.addressLine1.trim() &&
    !form.addressLine2.trim() &&
    !form.addressCity.trim() &&
    !form.addressRegion.trim() &&
    !form.addressPostalCode.trim() &&
    !form.addressCountryCode.trim();

  const mutation = useMutation({
    mutationFn: () => {
      /**
       * Only what changed is sent.
       *
       * PATCH means "these fields"; sending the whole form would overwrite a
       * value another user edited in the meantime with one this page loaded
       * before their change. The empty string is normalised to null, because
       * an untouched optional input and a cleared one are the same intent.
       */
      const changed: UpdatePersonRequest = {};
      if (form.firstName !== person.firstName) changed.firstName = form.firstName.trim();
      if (form.lastName !== person.lastName) changed.lastName = form.lastName.trim();
      if (form.middleName !== (person.middleName ?? ''))
        changed.middleName = form.middleName.trim() || null;
      if (form.preferredName !== (person.preferredName ?? ''))
        changed.preferredName = form.preferredName.trim() || null;
      if (form.birthDate !== (person.birthDate ?? '')) changed.birthDate = form.birthDate || null;
      if (form.gender !== person.gender) changed.gender = form.gender;
      if (form.maritalStatus !== person.maritalStatus) changed.maritalStatus = form.maritalStatus;
      if (form.documentType !== (person.documentType ?? ''))
        changed.documentType = form.documentType || null;
      if (form.documentNumber !== (person.documentNumber ?? ''))
        changed.documentNumber = form.documentNumber.trim() || null;
      if (form.status !== person.status) changed.status = form.status;
      if (form.communicationsOptOut !== person.communicationsOptOut)
        changed.communicationsOptOut = form.communicationsOptOut;
      if (addressChanged) {
        changed.address = addressIsBlank
          ? null
          : {
              line1: form.addressLine1.trim() || null,
              line2: form.addressLine2.trim() || null,
              city: form.addressCity.trim() || null,
              region: form.addressRegion.trim() || null,
              postalCode: form.addressPostalCode.trim() || null,
              countryCode: form.addressCountryCode.trim() || null,
            };
      }
      if (form.birthCountryCode !== (person.birthCountryCode ?? ''))
        changed.birthCountryCode = form.birthCountryCode || null;
      if (form.personalEmail !== (person.personalEmail ?? ''))
        changed.personalEmail = form.personalEmail.trim() || null;
      if (form.workEmail !== (person.workEmail ?? ''))
        changed.workEmail = form.workEmail.trim() || null;
      if (form.secondaryEmail !== (person.secondaryEmail ?? ''))
        changed.secondaryEmail = form.secondaryEmail.trim() || null;
      if (form.academicLevel !== initialAcademicLevel)
        changed.academicLevel = form.academicLevel || null;
      if (form.academicLevelStatus !== initialAcademicLevelStatus)
        changed.academicLevelStatus = form.academicLevelStatus || null;
      if (form.professionId !== (person.profession?.id ?? ''))
        changed.professionId = form.professionId || null;
      if (form.occupationId !== (person.occupation?.id ?? ''))
        changed.occupationId = form.occupationId || null;
      if (form.economicSectorId !== (person.economicSector?.id ?? ''))
        changed.economicSectorId = form.economicSectorId || null;
      if (form.bloodType !== initialBloodType) changed.bloodType = form.bloodType || null;
      if (form.medicalIndications !== (person.medicalIndications ?? ''))
        changed.medicalIndications = form.medicalIndications.trim() || null;

      return peopleApi.update(person.id, changed);
    },
    onSuccess: onSaved,
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        const fields = error.fieldErrors;
        setFieldErrors(fields);
        if (Object.keys(fields).length === 0) setFormError(describeError(error, t));
        return;
      }
      setFormError(t('errors.INTERNAL_ERROR'));
    },
  });

  const isUnchanged =
    form.firstName === person.firstName &&
    form.lastName === person.lastName &&
    form.middleName === (person.middleName ?? '') &&
    form.preferredName === (person.preferredName ?? '') &&
    form.birthDate === (person.birthDate ?? '') &&
    form.gender === person.gender &&
    form.maritalStatus === person.maritalStatus &&
    form.status === person.status &&
    form.communicationsOptOut === person.communicationsOptOut &&
    !addressChanged &&
    form.documentType === initialDocumentType &&
    form.documentNumber === (person.documentNumber ?? '') &&
    form.birthCountryCode === (person.birthCountryCode ?? '') &&
    form.personalEmail === (person.personalEmail ?? '') &&
    form.workEmail === (person.workEmail ?? '') &&
    form.secondaryEmail === (person.secondaryEmail ?? '') &&
    form.academicLevel === initialAcademicLevel &&
    form.academicLevelStatus === initialAcademicLevelStatus &&
    form.professionId === (person.profession?.id ?? '') &&
    form.occupationId === (person.occupation?.id ?? '') &&
    form.economicSectorId === (person.economicSector?.id ?? '') &&
    form.bloodType === initialBloodType &&
    form.medicalIndications === (person.medicalIndications ?? '');

  return (
    <form
      className="detail-form"
      noValidate
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        setFieldErrors({});
        setFormError(null);
        mutation.mutate();
      }}
    >
      {formError && <Alert tone="danger">{formError}</Alert>}

      <div className="form-grid">
        <Field
          label={t('people.person.firstName')}
          name="firstName"
          value={form.firstName}
          onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          error={fieldErrors.firstName}
          required
          autoFocus
        />
        <Field
          label={t('people.person.middleName')}
          name="middleName"
          value={form.middleName}
          onChange={(e) => setForm({ ...form, middleName: e.target.value })}
          error={fieldErrors.middleName}
        />
        <Field
          label={t('people.person.lastName')}
          name="lastName"
          value={form.lastName}
          onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          error={fieldErrors.lastName}
          required
        />
        <Field
          label={t('people.person.preferredName')}
          hint={t('people.person.preferredNameHint')}
          name="preferredName"
          value={form.preferredName}
          onChange={(e) => setForm({ ...form, preferredName: e.target.value })}
          error={fieldErrors.preferredName}
        />
        <Field
          label={t('people.person.birthDate')}
          name="birthDate"
          type="date"
          value={form.birthDate}
          onChange={(e) => setForm({ ...form, birthDate: e.target.value })}
          error={fieldErrors.birthDate}
        />
      </div>

      <div className="form-grid">
        <Select
          label={t('common.labels.gender')}
          name="gender"
          value={form.gender}
          options={GENDERS}
          translate={(value) => t(`people.person.gender.${value}`)}
          onChange={(value) => setForm({ ...form, gender: value })}
        />
        <Select
          label={t('common.labels.maritalStatus')}
          name="maritalStatus"
          value={form.maritalStatus}
          options={MARITAL_STATUSES}
          translate={(value) => t(`people.person.maritalStatus.${value}`)}
          onChange={(value) => setForm({ ...form, maritalStatus: value })}
        />
        <Select
          label={t('common.labels.documentType')}
          name="documentType"
          value={form.documentType}
          options={DOCUMENT_TYPE_OPTIONS}
          translate={(value) =>
            value ? t(`people.person.documentType.${value}`) : t('common.labels.none')
          }
          onChange={(value) => setForm({ ...form, documentType: value })}
        />
        <Field
          label={t('common.labels.documentNumber')}
          name="documentNumber"
          value={form.documentNumber}
          onChange={(e) => setForm({ ...form, documentNumber: e.target.value })}
          error={fieldErrors.documentNumber}
          dir="ltr"
        />
        <Select
          label={t('common.labels.status')}
          name="status"
          // ARCHIVED and DELETED are reached through their own dedicated
          // actions, each of which records a reason. Offering them here
          // would be a second way in that loses it.
          options={PERSON_STATUSES.filter((value) => value !== 'ARCHIVED' && value !== 'DELETED')}
          value={form.status}
          translate={(value) => t(`people.person.status.${value}`)}
          onChange={(value) => setForm({ ...form, status: value })}
        />
      </div>

      <div className="form-grid">
        <Field
          label={t('people.person.addressLine1')}
          name="addressLine1"
          value={form.addressLine1}
          onChange={(e) => setForm({ ...form, addressLine1: e.target.value })}
          error={fieldErrors['address.line1']}
        />
        <Field
          label={t('people.person.addressLine2')}
          name="addressLine2"
          value={form.addressLine2}
          onChange={(e) => setForm({ ...form, addressLine2: e.target.value })}
          error={fieldErrors['address.line2']}
        />
      </div>
      <div className="form-grid">
        <Field
          label={t('people.person.addressCity')}
          name="addressCity"
          value={form.addressCity}
          onChange={(e) => setForm({ ...form, addressCity: e.target.value })}
          error={fieldErrors['address.city']}
        />
        <Field
          label={t('people.person.addressRegion')}
          name="addressRegion"
          value={form.addressRegion}
          onChange={(e) => setForm({ ...form, addressRegion: e.target.value })}
          error={fieldErrors['address.region']}
        />
      </div>
      <div className="form-grid">
        <Field
          label={t('people.person.addressPostalCode')}
          name="addressPostalCode"
          value={form.addressPostalCode}
          onChange={(e) => setForm({ ...form, addressPostalCode: e.target.value })}
          error={fieldErrors['address.postalCode']}
          dir="ltr"
        />
        <Field
          label={t('people.person.addressCountryCode')}
          name="addressCountryCode"
          value={form.addressCountryCode}
          onChange={(e) =>
            setForm({ ...form, addressCountryCode: e.target.value.toUpperCase().slice(0, 2) })
          }
          error={fieldErrors['address.countryCode']}
          placeholder={t('people.person.addressCountryCodeHint')}
          dir="ltr"
        />
      </div>

      <div className="form-grid">
        <Select
          label={t('common.labels.birthCountry')}
          name="birthCountryCode"
          value={form.birthCountryCode}
          options={['', ...COUNTRY_CODES] as const}
          translate={(value) => (value ? countryName(value, locale) : t('common.labels.none'))}
          onChange={(value) => setForm({ ...form, birthCountryCode: value })}
        />
      </div>

      <div className="tab-fieldset">
        <h3 className="detail-section-title">{t('people.contact.title')}</h3>
        <div className="form-grid">
          <Field
            label={t('common.labels.personalEmail')}
            name="personalEmail"
            type="email"
            value={form.personalEmail}
            onChange={(e) => setForm({ ...form, personalEmail: e.target.value })}
            error={fieldErrors.personalEmail}
            dir="ltr"
          />
          <Field
            label={t('common.labels.workEmail')}
            name="workEmail"
            type="email"
            value={form.workEmail}
            onChange={(e) => setForm({ ...form, workEmail: e.target.value })}
            error={fieldErrors.workEmail}
            dir="ltr"
          />
        </div>
        <Field
          label={t('common.labels.secondaryEmail')}
          name="secondaryEmail"
          type="email"
          value={form.secondaryEmail}
          onChange={(e) => setForm({ ...form, secondaryEmail: e.target.value })}
          error={fieldErrors.secondaryEmail}
          dir="ltr"
        />
      </div>

      <div className="tab-fieldset">
        <h3 className="detail-section-title">{t('people.academicWork.title')}</h3>
        <div className="form-grid">
          <Select
            label={t('common.labels.academicLevel')}
            name="academicLevel"
            value={form.academicLevel}
            options={['', ...PERSON_ACADEMIC_LEVELS] as const}
            translate={(value) =>
              value ? t(`people.person.academicLevel.${value}`) : t('common.labels.none')
            }
            onChange={(value) => setForm({ ...form, academicLevel: value })}
          />
          <Select
            label={t('common.labels.academicLevelStatus')}
            name="academicLevelStatus"
            value={form.academicLevelStatus}
            options={['', ...PERSON_ACADEMIC_LEVEL_STATUSES] as const}
            translate={(value) =>
              value ? t(`people.person.academicLevelStatus.${value}`) : t('common.labels.none')
            }
            onChange={(value) => setForm({ ...form, academicLevelStatus: value })}
          />
        </div>
        <div className="form-grid">
          <CatalogPicker
            kind="profession"
            label={t('common.labels.profession')}
            value={form.professionId}
            onChange={(id) => setForm({ ...form, professionId: id })}
          />
          <CatalogPicker
            kind="occupation"
            label={t('common.labels.occupation')}
            value={form.occupationId}
            onChange={(id) => setForm({ ...form, occupationId: id })}
          />
        </div>
        <CatalogPicker
          kind="economic-sector"
          label={t('common.labels.economicSector')}
          value={form.economicSectorId}
          onChange={(id) => setForm({ ...form, economicSectorId: id })}
        />
      </div>

      <div className="tab-fieldset">
        <h3 className="detail-section-title">{t('people.medical.title')}</h3>
        <div className="form-grid">
          <Select
            label={t('common.labels.bloodType')}
            name="bloodType"
            value={form.bloodType}
            options={['', ...PERSON_BLOOD_TYPES] as const}
            translate={(value) =>
              value ? t(`people.person.bloodType.${value}`) : t('common.labels.none')
            }
            onChange={(value) => setForm({ ...form, bloodType: value })}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="medicalIndications">
            {t('common.labels.medicalIndications')}
          </label>
          <textarea
            id="medicalIndications"
            className="field-input"
            rows={3}
            value={form.medicalIndications}
            onChange={(e) => setForm({ ...form, medicalIndications: e.target.value })}
          />
        </div>
      </div>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={form.communicationsOptOut}
          onChange={(event) => setForm({ ...form, communicationsOptOut: event.target.checked })}
        />
        <span>{t('people.person.communicationsOptOut')}</span>
      </label>

      <footer className="form-actions">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.actions.cancel')}
        </Button>
        <Button type="submit" variant="primary" loading={mutation.isPending} disabled={isUnchanged}>
          {t('common.actions.save')}
        </Button>
      </footer>
    </form>
  );
}

function Select<T extends string>({
  label,
  name,
  value,
  options,
  translate,
  onChange,
}: {
  label: string;
  name: string;
  value: T;
  options: readonly T[];
  translate: (value: T) => string;
  onChange: (value: T) => void;
}): ReactNode {
  const id = `field-${name}`;
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        name={name}
        className="field-input"
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {translate(option)}
          </option>
        ))}
      </select>
    </div>
  );
}

/** A one-line rendering of an address, or null when nothing is on file. */
function formatAddress(address: AddressResponse | null): string | null {
  if (!address) return null;
  const parts = [
    address.line1,
    address.line2,
    [address.city, address.region].filter(Boolean).join(', '),
    address.postalCode,
    address.countryCode,
  ].filter((part) => part && part.trim().length > 0);
  return parts.length > 0 ? parts.join(', ') : null;
}

/** Maps an API error code to a translated message, never showing the raw one. */
function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}
